/**
 * Haftalık takvim.
 *
 * Saat ızgarası yerine GÜN SÜTUNLARI seçildi. Bir merkezde randevular
 * 09:00–19:00 arasında ve çoğu 50 dakika; saat ızgarasının boş
 * satırları ekranın üçte ikisini kaplıyor ve asıl bakılan şeyi —
 * "bugün kimler var, kim gelmedi" — küçültüyor. Sütun listesi aynı
 * bilgiyi daha az yerde veriyor.
 *
 * Çakışma uyarısı burada gösteriliyor ama ENGELLEMİYOR: grup seansında
 * aynı uygulayıcının aynı saatte birden çok danışanı olur. Oda
 * çakışmasını ise veritabanı reddediyor ve o geçilemiyor.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ApiHatasi,
  RANDEVU_DURUMU,
  api,
  type Cakisan,
  type DanisanOzet,
  type Randevu,
  type Yerler,
} from '../api'

const GUNLER = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar']

/** Haftanın pazartesi 00:00'ı — hafta pazartesi başlar */
function haftaBasi(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  const gun = (x.getDay() + 6) % 7
  x.setDate(x.getDate() - gun)
  return x
}

const saat = (iso: string) =>
  new Date(iso).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })

const gunYaz = (d: Date) => d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })

export function Takvim({
  yazabilir,
  onDanisan,
}: {
  yazabilir: boolean
  onDanisan: (id: string) => void
}) {
  const [bas, setBas] = useState(() => haftaBasi(new Date()))
  const [randevular, setRandevular] = useState<Randevu[] | null>(null)
  const [yerler, setYerler] = useState<Yerler | null>(null)
  const [suzgec, setSuzgec] = useState('')
  const [formAcik, setFormAcik] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  const bit = new Date(bas.getTime() + 7 * 24 * 3600 * 1000)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const r = await api.randevular(
        bas.toISOString(),
        new Date(bas.getTime() + 7 * 24 * 3600 * 1000).toISOString(),
        suzgec || undefined,
      )
      setRandevular(r.randevular)
    } catch {
      setHata('Randevular alınamadı.')
      setRandevular([])
    }
  }, [bas, suzgec])

  useEffect(() => {
    void yukle()
  }, [yukle])

  useEffect(() => {
    api
      .yerler()
      .then(setYerler)
      .catch(() => setYerler(null))
  }, [])

  const durumDegis = async (r: Randevu, durum: string) => {
    try {
      await api.randevuDurum(r.id, durum)
      await yukle()
    } catch {
      setHata('Durum değiştirilemedi.')
    }
  }

  const iptal = async (r: Randevu) => {
    const seri = Boolean(r.seriId)
    try {
      const s = await api.randevuDurum(r.id, 'cancelled', undefined, seri)
      await yukle()
      if (s.seriIptal > 0) {
        setHata(null)
      }
    } catch {
      setHata('İptal edilemedi.')
    }
  }

  return (
    <>
      <h1>Takvim</h1>
      <p className="alt">
        {gunYaz(bas)} – {gunYaz(new Date(bit.getTime() - 1))}
      </p>

      {hata && <div className="uyari">{hata}</div>}

      <div className="dugmeler" style={{ marginBottom: 14 }}>
        <button onClick={() => setBas(new Date(bas.getTime() - 7 * 24 * 3600 * 1000))}>
          ← Önceki
        </button>
        <button onClick={() => setBas(haftaBasi(new Date()))}>Bu hafta</button>
        <button onClick={() => setBas(new Date(bas.getTime() + 7 * 24 * 3600 * 1000))}>
          Sonraki →
        </button>
        {yerler && yerler.uygulayicilar.length > 1 && (
          <select
            value={suzgec}
            onChange={(e) => setSuzgec(e.target.value)}
            style={{ width: 220 }}
          >
            <option value="">Tüm uygulayıcılar</option>
            {yerler.uygulayicilar.map((u) => (
              <option key={u.id} value={u.id}>
                {u.unvan ? u.unvan + ' ' : ''}
                {u.adSoyad}
              </option>
            ))}
          </select>
        )}
        {yazabilir && (
          <button
            className="birincil"
            style={{ marginLeft: 'auto' }}
            onClick={() => setFormAcik((v) => !v)}
          >
            {formAcik ? 'Vazgeç' : 'Randevu ekle'}
          </button>
        )}
      </div>

      {formAcik && yerler && (
        <RandevuFormu
          yerler={yerler}
          onIptal={() => setFormAcik(false)}
          onKayit={() => {
            setFormAcik(false)
            void yukle()
          }}
        />
      )}

      {randevular === null ? (
        <p className="alt">Yükleniyor…</p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: 10,
          }}
        >
          {GUNLER.map((ad, i) => {
            const gun = new Date(bas.getTime() + i * 24 * 3600 * 1000)
            const gunun = randevular.filter(
              (r) => new Date(r.baslangic).toDateString() === gun.toDateString(),
            )
            const bugun = gun.toDateString() === new Date().toDateString()
            return (
              <div
                key={ad}
                className="kart"
                style={{
                  marginBottom: 0,
                  borderColor: bugun ? 'var(--accent)' : 'var(--line)',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {ad}
                  <span className="alt" style={{ margin: '0 0 0 6px', fontWeight: 400 }}>
                    {gunYaz(gun)}
                  </span>
                </div>
                {gunun.length === 0 ? (
                  <p className="alt" style={{ margin: '10px 0 0', fontSize: 13 }}>
                    —
                  </p>
                ) : (
                  gunun.map((r) => (
                    <div
                      key={r.id}
                      style={{
                        marginTop: 10,
                        paddingTop: 10,
                        borderTop: '1px solid var(--line)',
                        opacity: r.durum === 'cancelled' ? 0.5 : 1,
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {saat(r.baslangic)}–{saat(r.bitis)}
                        {r.seriId && (
                          <span className="alt" style={{ margin: '0 0 0 6px', fontSize: 11 }}>
                            seri
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => onDanisan(r.danisanId)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          font: 'inherit',
                          color: 'var(--accent)',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        {r.danisanAd} {r.danisanSoyad}
                      </button>
                      <div className="alt" style={{ margin: '2px 0 0', fontSize: 12 }}>
                        {r.uygulayici ?? '—'}
                        {r.oda ? ` · ${r.oda}` : ''}
                      </div>
                      <div style={{ marginTop: 6 }}>
                        <span
                          className={
                            'rozet' +
                            (r.durum === 'attended'
                              ? ' ok'
                              : r.durum === 'no_show'
                                ? ' warn'
                                : r.durum === 'cancelled'
                                  ? ' bad'
                                  : '')
                          }
                        >
                          {RANDEVU_DURUMU[r.durum] ?? r.durum}
                        </span>
                      </div>
                      {yazabilir && r.durum === 'scheduled' && (
                        <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                          <button
                            style={{ padding: '4px 8px', fontSize: 12 }}
                            onClick={() => void durumDegis(r, 'attended')}
                          >
                            Geldi
                          </button>
                          <button
                            style={{ padding: '4px 8px', fontSize: 12 }}
                            onClick={() => void durumDegis(r, 'no_show')}
                          >
                            Gelmedi
                          </button>
                          <button
                            style={{ padding: '4px 8px', fontSize: 12 }}
                            onClick={() => void iptal(r)}
                          >
                            İptal
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

/* ------------------------------------------------------------------ */

function RandevuFormu({
  yerler,
  onIptal,
  onKayit,
}: {
  yerler: Yerler
  onIptal: () => void
  onKayit: () => void
}) {
  const [danisanlar, setDanisanlar] = useState<DanisanOzet[]>([])
  const [danisanId, setDanisanId] = useState('')
  const [uygulayiciId, setUygulayiciId] = useState(yerler.uygulayicilar[0]?.id ?? '')
  const [odaId, setOdaId] = useState('')
  const [tarih, setTarih] = useState(() => new Date().toISOString().slice(0, 10))
  const [saatAlan, setSaat] = useState('10:00')
  const [dakika, setDakika] = useState(50)
  const [tekrar, setTekrar] = useState(1)
  const [cakisanlar, setCakisanlar] = useState<Cakisan[] | null>(null)
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  useEffect(() => {
    api
      .danisanlar()
      .then((r) => {
        setDanisanlar(r.danisanlar)
        setDanisanId((d) => d || (r.danisanlar[0]?.id ?? ''))
      })
      .catch(() => setDanisanlar([]))
  }, [])

  const baslangic = () => new Date(`${tarih}T${saatAlan}:00`).toISOString()

  const kaydet = async (onayla: boolean) => {
    setHata(null)
    setBekliyor(true)
    try {
      await api.randevuEkle({
        danisanId,
        uygulayiciId,
        odaId: odaId || null,
        baslangic: baslangic(),
        dakika,
        tekrarHafta: tekrar,
        onayla,
      })
      onKayit()
    } catch (e) {
      if (e instanceof ApiHatasi && e.kod === 'cakisma') {
        setCakisanlar((e.govde?.['cakisanlar'] as Cakisan[] | undefined) ?? [])
        setHata('Bu saatte çakışma var.')
      } else if (e instanceof ApiHatasi && e.kod === 'oda_dolu') {
        setHata('Seçtiğiniz oda o saatte dolu. Aynı odaya ikinci randevu girilemiyor.')
      } else {
        setHata('Randevu kaydedilemedi.')
      }
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <div className="kart">
      <h2 style={{ marginTop: 0 }}>Yeni randevu</h2>
      {hata && <div className="uyari">{hata}</div>}

      {cakisanlar && cakisanlar.length > 0 && (
        <div className="kisit" style={{ marginTop: 0, marginBottom: 14 }}>
          <strong>Çakışma:</strong>{' '}
          {cakisanlar
            .map((c) => `${saat(c.baslangic)} ${c.danisanAd} ${c.danisanSoyad}`)
            .join(', ')}
          . Grup seansıysa devam edebilirsiniz; oda çakışmasıysa sistem kabul etmez.
        </div>
      )}

      <div className="satir">
        <label>
          Danışan
          <select value={danisanId} onChange={(e) => setDanisanId(e.target.value)}>
            {danisanlar.map((d) => (
              <option key={d.id} value={d.id}>
                {d.ad} {d.soyad}
              </option>
            ))}
          </select>
        </label>
        <label>
          Uygulayıcı
          <select value={uygulayiciId} onChange={(e) => setUygulayiciId(e.target.value)}>
            {yerler.uygulayicilar.map((u) => (
              <option key={u.id} value={u.id}>
                {u.adSoyad}
              </option>
            ))}
          </select>
        </label>
        <label>
          Oda
          <select value={odaId} onChange={(e) => setOdaId(e.target.value)}>
            <option value="">Oda seçilmedi</option>
            {yerler.odalar.map((o) => (
              <option key={o.id} value={o.id}>
                {o.ad}
              </option>
            ))}
          </select>
        </label>
        <label>
          Tarih
          <input type="date" value={tarih} onChange={(e) => setTarih(e.target.value)} />
        </label>
        <label>
          Saat
          <input type="time" value={saatAlan} onChange={(e) => setSaat(e.target.value)} />
        </label>
        <label>
          Süre (dakika)
          <input
            type="number"
            min={5}
            max={480}
            step={5}
            value={dakika}
            onChange={(e) => setDakika(Number(e.target.value))}
          />
        </label>
        <label>
          Kaç hafta tekrar
          <input
            type="number"
            min={1}
            max={52}
            value={tekrar}
            onChange={(e) => setTekrar(Math.max(1, Math.min(52, Number(e.target.value))))}
          />
        </label>
      </div>

      {yerler.odalar.length === 0 && (
        <div className="bilgi">
          Tanımlı oda yok. Oda seçmeden randevu yazılabilir ama oda çakışması kontrol
          edilemez.
        </div>
      )}

      <div className="dugmeler">
        <button
          className="birincil"
          disabled={bekliyor || !danisanId || !uygulayiciId}
          onClick={() => void kaydet(false)}
        >
          {bekliyor ? 'Kaydediliyor…' : tekrar > 1 ? `${tekrar} hafta kaydet` : 'Kaydet'}
        </button>
        {cakisanlar && cakisanlar.length > 0 && (
          <button disabled={bekliyor} onClick={() => void kaydet(true)}>
            Çakışmaya rağmen kaydet
          </button>
        )}
        <button onClick={onIptal} disabled={bekliyor}>
          Vazgeç
        </button>
      </div>
    </div>
  )
}
